import React, { useState, useRef, useCallback } from 'react';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import {
  Button,
  GalleryCardContent,
  GalleryCatalog,
  KitchenDownloadCatalog,
  Section,
  SectionSubTitle,
  SelectionGroup,
  SelectionGroupOption,
  SelectionGroupValue,
} from 'common/components';
import { useIntersection } from 'common/hooks';
import {
  COLLECTIONS_STORE_NAME,
  FacetValue,
  FETCH_COLLECTIONS,
  KitchenCollection,
  selectAvailableCollections,
  selectStyleFacet,
} from 'modules/collections';
import { analyticsUtils } from 'utils/analytics-utils';
import {
  kitchenGalleryNeoStyle,
  kitchenGallerySectionMoreBtn,
  kitchenGallerySectionTabs,
} from './kitchen-gallery-styles';

const INITIAL_IMAGES_PER_PAGE = 5;
const IMAGES_PER_PAGE = 6;

const getSelectionGroupList = (styles: FacetValue[]): SelectionGroupOption[] =>
  styles.map(({ name, value }) => ({
    text: value as string,
    value: name,
  }));

type KitchenGalleryProps = {
  elementBlock: string;
  isRuRegion: boolean;
  title?: string;
}

export const KitchenGallery: React.FC<KitchenGalleryProps> = ({ elementBlock, isRuRegion, title }) => {
  const { collections, dispatch } = useStoreon<State>(COLLECTIONS_STORE_NAME);
  const productRef = useRef<HTMLAnchorElement[]>([]);
  const collectionList = selectAvailableCollections(collections) as KitchenCollection[];
  const styles = selectStyleFacet(collections);
  const selectionGroupList = getSelectionGroupList(styles?.values || []);

  const [activeSelectionGroup, setActiveSelectionGroup] = useState<string>('all');
  const [imagePerPage, setImagePerPage] = useState<number>(INITIAL_IMAGES_PER_PAGE);

  const { offset, productsCount } = collections;

  useIntersection(productRef, el => {
    const position = el.getAttribute('data-position');
    const numberPosition = Number(position);

    if (isNaN(numberPosition)) {
      return;
    }

    const collection = collectionList[numberPosition];
    const collectionInfo = Array.isArray(collection) ? collection[0] : collection;

    analyticsUtils.sendEvent('GA:impressions', {
      elementType: 'product',
      elementBlock: `${elementBlock} / Catalog`,
      division: 'Кухни',
      subdivision: 'Проект кухни как пример',
      komplektyId: collectionInfo.productId,
      productName: collectionInfo.displayedName,
      productId: collectionInfo.productId,
      productPrice: collectionInfo.price?.price,
      productPosition: numberPosition + 1,
      productTag: undefined,
    });
  });

  const handleTabChange = (_: React.MouseEvent, value: SelectionGroupValue) => {
    if (value !== activeSelectionGroup) {
      setImagePerPage(INITIAL_IMAGES_PER_PAGE);
      setActiveSelectionGroup(value as string);

      dispatch(FETCH_COLLECTIONS.clear);
      dispatch(FETCH_COLLECTIONS.init, {
        url: 'get-kitchen-price',
        offset: 0,
        limit: INITIAL_IMAGES_PER_PAGE,
        variantId: value,
        landingType: 'new-kitchen',
      });

      const activeOption: SelectionGroupOption | undefined = selectionGroupList.find(
        (option: SelectionGroupOption) => option.value === value
      );
      if (activeOption) {
        analyticsUtils.clickButtonEvent(
          `${elementBlock} / Catalog`,
          activeOption.text,
          'Каталог кухонь'
        );
      }
    }
  };

  const handleClickShowMoreBtn = () => {
    setImagePerPage(imagePerPage + IMAGES_PER_PAGE);
    dispatch(FETCH_COLLECTIONS.init, {
      url: 'get-kitchen-price',
      limit: IMAGES_PER_PAGE,
      offset: offset === 0 ? INITIAL_IMAGES_PER_PAGE : offset + IMAGES_PER_PAGE,
      variantId: activeSelectionGroup,
      landingType: 'new-kitchen',
    });
    analyticsUtils.clickButtonEvent(`${elementBlock} / Catalog`, 'Показать ещё', 'Каталог кухонь');
  };

  const handleCardClick = useCallback((collection: KitchenCollection, position: number) => {
    analyticsUtils.sendEvent('GA:clickProduct', {
      elementType: 'product',
      elementBlock: `${elementBlock} / Catalog`,
      elementName: 'productNameLink',
      division: 'Кухни',
      subdivision: 'Проект кухни как пример',
      komplektyId: collection.productId,
      productName: collection.displayedName,
      productId: collection.productId,
      productPrice: collection.price,
      productPosition: position + 1,
      productTag: undefined,
    });
  }, [elementBlock]);

  const handleCollectionPreviewClick = (ckickedCollection: KitchenCollection) => {
    analyticsUtils.clickButtonEvent(`${elementBlock} / Catalog`, ckickedCollection.displayedName, 'Цвет фасада');
  };

  return (
    <Section>
      <SectionSubTitle margin="l">
        {title}
      </SectionSubTitle>

      <SelectionGroup
        className={kitchenGallerySectionTabs}
        active={activeSelectionGroup}
        onChange={handleTabChange}
        optionList={selectionGroupList}
        rounded
      />

      {collectionList.length > 0 && (
        <GalleryCatalog
          className={activeSelectionGroup === 'neo' ? kitchenGalleryNeoStyle : ''}
          catalog={collectionList as KitchenCollection[]}
          cardRefCb={(el: HTMLAnchorElement | null) => {
            const position = el?.getAttribute('data-position');
            if (position && el) {
              productRef.current[+position] = el;
            }
          }}
          downloadCatalog={
            <KitchenDownloadCatalog
              elementBlock={`${elementBlock} / Catalog`}
              elementName="Каталог кухонь"
              isNewKitchen
            />
          }
          renderCardContent={({ displayedName, price }) => (
            <GalleryCardContent
              currency={isRuRegion ? '₽' : '₸'}
              displayedName={displayedName}
              price={price.price}
              withCredit={isRuRegion}
            />
          )}
          onCardClick={handleCardClick}
          onCollectionPreviewClick={handleCollectionPreviewClick}
        />
      )}

      {IMAGES_PER_PAGE + offset <= productsCount && (
        <Button
          className={kitchenGallerySectionMoreBtn}
          rounded
          onClick={handleClickShowMoreBtn}
          adaptive={false}>
          Показать ещё
        </Button>
      )}
    </Section>
  );
};
